/**
 * Client-side mirror of the server's estate ripeness classifier · used
 * to preview the ripeness class WHILE the Mandor is editing signals in
 * the correction UI. The server re-runs the same rules on save and its
 * result is authoritative.
 *
 * Keep this file in lock-step with
 *   artifacts/api-server/src/services/estate_ripeness_classifier.ts
 */

export const OLC_VALUES = [
  "black",
  "black_purple",
  "dark_purple",
  "dark_reddish",
  "reddish_purple",
  "purple",
  "reddish",
  "reddish_orange",
  "orange",
  "dull_orange",
] as const;
export type Olc = (typeof OLC_VALUES)[number];

export const MESOCARP_VALUES = [
  "pale_green",
  "pale_yellow",
  "yellow",
  "yellow_orange",
  "orange",
] as const;
export type Mesocarp = (typeof MESOCARP_VALUES)[number];

export const DEFECT_KEYS = [
  "rotten",
  "buah_peram",
  "old",
  "wet",
  "dirty",
  "long_stalk",
  "reject",
] as const;

/**
 * Categorical socket buckets · the six choices a Mandor picks per bunch.
 * `sockets` / `pct` are what we feed the classifier; the label is what
 * appears in the dropdown.
 */
export interface SocketBucket {
  key: string;
  label: string;
  sockets: number;
  pct?: number;
}
export const SOCKET_BUCKETS: readonly SocketBucket[] = [
  { key: "0", label: "0", sockets: 0, pct: 0 },
  { key: "1_4", label: "1 – 4", sockets: 2 },
  { key: "gte5", label: "≥ 5", sockets: 10 },
  { key: "gt50", label: "> 50%", sockets: 15, pct: 60 },
  { key: "50_90", label: "50% – 90%", sockets: 20, pct: 70 },
  { key: "gt90", label: "> 90%", sockets: 30, pct: 95 },
] as const;

/** Try to identify which bucket best matches a stored (sockets, pct). */
export function socketBucketFor(sockets: number, pct?: number): string {
  if (typeof pct === "number" && pct > 90) return "gt90";
  if (typeof pct === "number" && pct >= 50) return pct > 50 && pct <= 60 ? "gt50" : "50_90";
  if (sockets === 0) return "0";
  if (sockets <= 4) return "1_4";
  return "gte5";
}

/** Human-friendly OLC dropdown options. Order matches the flowchart. */
export const OLC_UI_OPTIONS: readonly { label: string; value: Olc }[] = [
  { label: "Black", value: "black" },
  { label: "Black Purple", value: "black_purple" },
  { label: "Dark Purple", value: "dark_purple" },
  { label: "Dark Reddish", value: "dark_reddish" },
  { label: "Reddish Purple", value: "reddish_purple" },
  { label: "Purple", value: "purple" },
  { label: "Reddish", value: "reddish" },
  { label: "Reddish Orange", value: "reddish_orange" },
  { label: "Orange", value: "orange" },
  { label: "Dull Orange", value: "dull_orange" },
] as const;

/**
 * Mesocarp dropdown options. "Pale Green/Pale Yellow" is a combined
 * choice at the UI level — both map to the same UNRIPE band in the
 * classifier, so we canonicalise to "pale_green" on write.
 */
export const MESOCARP_UI_OPTIONS: readonly {
  label: string;
  value: Mesocarp;
}[] = [
  { label: "Pale Green / Pale Yellow", value: "pale_green" },
  { label: "Yellow", value: "yellow" },
  { label: "Yellowish Orange", value: "yellow_orange" },
  { label: "Orange", value: "orange" },
] as const;

export type RipenessClass = "RIPE" | "UNRIPE" | "OVERRIPE" | "EMPTY";

export interface BunchSignal {
  detachedSockets: number;
  detachedPct?: number;
  olc: Olc | null;
  mesocarp: Mesocarp | null;
}

const RIPE_OLC = new Set<Olc>([
  "dark_purple",
  "dark_reddish",
  "reddish_purple",
  "purple",
  "reddish",
  "reddish_orange",
  "orange",
]);
const RIPE_MESO = new Set<Mesocarp>(["yellow_orange", "orange"]);
const UNRIPE_OLC = new Set<Olc>([
  "black",
  "black_purple",
  "dark_purple",
  "dark_reddish",
  "reddish_purple",
  "reddish",
  "reddish_orange",
]);
const UNRIPE_MESO = new Set<Mesocarp>([
  "pale_green",
  "pale_yellow",
  "yellow",
  "yellow_orange",
  "orange",
]);
const OVERRIPE_OLC = new Set<Olc>([
  "dark_reddish",
  "dull_orange",
  "orange",
  "reddish",
]);
const EMPTY_OLC = new Set<Olc>(["dark_reddish", "dull_orange", "reddish"]);
const EMPTY_MESO = new Set<Mesocarp>(["orange", "yellow_orange"]);

export function classifyBunch(signal: BunchSignal): {
  ripeness: RipenessClass;
  reason: string;
} {
  const pct = typeof signal.detachedPct === "number" ? signal.detachedPct : -1;

  if (
    pct > 90 &&
    signal.mesocarp &&
    EMPTY_MESO.has(signal.mesocarp) &&
    signal.olc &&
    EMPTY_OLC.has(signal.olc)
  ) {
    return { ripeness: "EMPTY", reason: `${pct}% detached · kosong band` };
  }
  if (
    pct > 50 &&
    pct <= 90 &&
    signal.mesocarp === "orange" &&
    signal.olc &&
    OVERRIPE_OLC.has(signal.olc)
  ) {
    return { ripeness: "OVERRIPE", reason: `${pct}% detached · overripe band` };
  }
  if (
    signal.detachedSockets >= 5 &&
    signal.mesocarp &&
    RIPE_MESO.has(signal.mesocarp) &&
    signal.olc &&
    RIPE_OLC.has(signal.olc)
  ) {
    return {
      ripeness: "RIPE",
      reason: `${signal.detachedSockets} sockets · ripe band`,
    };
  }
  if (
    signal.detachedSockets <= 4 &&
    signal.mesocarp &&
    UNRIPE_MESO.has(signal.mesocarp) &&
    signal.olc &&
    UNRIPE_OLC.has(signal.olc)
  ) {
    return {
      ripeness: "UNRIPE",
      reason: `${signal.detachedSockets} sockets · unripe band`,
    };
  }
  // Fallbacks
  if (pct > 90) return { ripeness: "EMPTY", reason: `${pct}% detached (colour ambiguous)` };
  if (pct > 50) return { ripeness: "OVERRIPE", reason: `${pct}% detached (colour ambiguous)` };
  if (signal.detachedSockets >= 5)
    return { ripeness: "RIPE", reason: `${signal.detachedSockets} sockets (colour ambiguous)` };
  return { ripeness: "UNRIPE", reason: `${signal.detachedSockets} sockets (default)` };
}
